import { useTheme } from "next-themes";
import { Toaster as Sonner } from "sonner";
import { useTranslation } from "@/shared/i18n";

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme();
  const { t } = useTranslation();

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group ph-no-capture"
      position="bottom-right"
      closeButton
      // Every toast now shares one corner, so the default window of 3 would
      // let a burst of notifications bury the export toast, which never
      // expires and carries the only download link. The offset clears the
      // support chat launcher.
      visibleToasts={5}
      offset={{ bottom: "96px", right: "24px" }}
      mobileOffset={{ bottom: "96px", right: "16px" }}
      toastOptions={{
        closeButtonAriaLabel: t("Close"),
        classNames: {
          // Sonner lays a toast out as one flex row, which parks the action
          // beside the text and leaves the wording a narrow column to wrap in.
          // A two-column grid keeps the icon in its own column and drops the
          // actions onto a second row under the content. The layout utilities
          // are marked important because Sonner injects its own rules at
          // runtime, at a specificity no Tailwind class reaches on its own.
          toast:
            "group toast grid! grid-cols-[auto_minmax(0,1fr)]! gap-x-0! gap-y-3! group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          // The gap to the content sits on the icon rather than on the grid, so
          // a toast without an icon is not indented by an empty first column.
          icon: "me-2.5!",
          content: "col-start-2!",
          description: "group-[.toast]:text-muted-foreground",
          // Auto margins beat justify-self on a grid item, and Sonner uses one
          // to push the action to the far end of the row.
          actionButton:
            "col-start-2! mx-0! justify-self-start! group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "col-start-2! mx-0! justify-self-start! group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
